import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable } from '@angular/core';
import {
  injectMutation,
  injectQuery,
  QueryClient,
} from '@tanstack/angular-query-experimental';
import { lastValueFrom, take } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '../../../../environments/environment';
import { AuthService } from '../../auth/services/auth.service';
import { SubdomainService } from '../../../core/services/subdomain.service';
import { auth as AuthGen } from '@valvur/shared-grpc';

@Injectable({ providedIn: 'root' })
export class UserSharedService {
  #http = inject(HttpClient);
  #authService = inject(AuthService);
  #translate = inject(TranslateService);
  #queryClient = inject(QueryClient);
  #subdomain = inject(SubdomainService);

  getBrowserLanguage(): string {
    const browserLang = this.#translate.getBrowserLang() ?? 'en';
    return browserLang.match(/en|et|ru/) ? browserLang : 'en';
  }

  settings = computed(() => this.#settingsQuery.data());

  #settingsQuery = injectQuery(() => ({
    queryKey: ['settings', this.getBrowserLanguage()],
    retry: 1,
    queryFn: () =>
      lastValueFrom(
        this.#http
          .get<AuthGen.UserSettings>(`${environment.baseUrl}/user/settings`, {
            params: { language: this.getBrowserLanguage() },
          })
          .pipe(take(1)),
      ),
    enabled: this.#authService.isAuthenticated(),
  }));

  updateSettings = injectMutation(() => ({
    mutationFn: (dto: Partial<AuthGen.UpdateSettingsDto>) =>
      lastValueFrom(
        this.#http.patch<{
          userId: string;
          language: string;
          [key: string]: unknown;
        }>(`${environment.baseUrl}/user/settings`, dto),
      ),
    onSuccess: () => {
      this.#queryClient.invalidateQueries({ queryKey: ['settings'] });
      this.invalidateProfileQueries();
    },
  }));

  uploadAvatar = injectMutation(() => ({
    mutationFn: (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      return lastValueFrom(
        this.#http.patch<AuthGen.User>(
          `${environment.baseUrl}/user/avatar`,
          formData,
        ),
      );
    },
    onSuccess: () => {
      this.invalidateProfileQueries();
    },
  }));

  updatePersonalInfo = injectMutation(() => ({
    mutationFn: (dto: Partial<AuthGen.InfoDto>) =>
      lastValueFrom(
        this.#http.patch<Partial<AuthGen.User>>(
          `${environment.baseUrl}/user/info`,
          dto,
        ),
      ),
    onSuccess: () => {
      this.invalidateProfileQueries();
    },
  }));

  deleteAvatar = injectMutation(() => ({
    mutationFn: () =>
      lastValueFrom(
        this.#http.delete<{ status: boolean }>(
          `${environment.baseUrl}/user/avatar`,
        ),
      ),
    onSuccess: () => {
      this.invalidateProfileQueries();
    },
  }));

  private invalidateProfileQueries(): void {
    if (this.#subdomain.isFamilySubdomain()) {
      this.#queryClient.invalidateQueries({ queryKey: ['user'] });
    }
    if (this.#subdomain.isSchoolSubdomain()) {
      this.#queryClient.invalidateQueries({ queryKey: ['teacher'] });
    }
    if (this.#subdomain.isAdminSubdomain()) {
      this.#queryClient.invalidateQueries({ queryKey: ['admin'] });
    }
    if (this.#subdomain.isPatrolSubdomain()) {
      this.#queryClient.invalidateQueries({ queryKey: ['patrol', 'profile'] });
    }
  }
}
